<template>
    <div>
        <SnackbarComponent v-model="snackbar.show" :message="snackbar.message" :color="snackbar.color" />
        <ImageCropperDialog v-model="cropDialog" :image-file="cropFile" @cropped="handleCropped"
            @cancel="handleCropCancel" />
        <v-dialog v-model="dialog" max-width="1300" persistent>
            <v-card class="blog-dialog">
                <v-card-title class="text-h5 pa-5">
                    Edit Blog Post
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-0 d-flex flex-column">
                    <v-form ref="form" class="h-100 d-flex flex-column">
                        <v-row no-gutters class="dialog-content flex-nowrap">
                            <!-- LEFT -->
                            <v-col cols="12" md="4" class="pa-5 left-panel" style="border-right:1px solid #e0e0e0;">

                                <v-text-field v-model="formData.blog_author_name" label="Author Name *"
                                    variant="outlined" density="compact"
                                    :rules="[(v) => !!v || 'Author Name is required']" class="mb-2" />

                                <v-text-field v-model="formData.blog_main_title" label="Blog Title *" variant="outlined"
                                    density="compact" :rules="[(v) => !!v || 'Blog Title is required']" class="mb-2" />

                                <!-- Publish Date -->
                                <v-text-field v-model="publishDateDisplay" label="Publish Date *"
                                    placeholder="MM/DD/YYYY" variant="outlined" density="compact" maxlength="10"
                                    class="mb-2" @input="(e) => formatDateInput(e)" @blur="validateAndFormatDate">
                                    <template #append-inner>
                                        <v-menu v-model="showPublishPicker" :close-on-content-click="false">
                                            <template #activator="{ props }">
                                                <v-icon v-bind="props" @mousedown.prevent>
                                                    mdi-calendar
                                                </v-icon>
                                            </template>

                                            <v-date-picker v-model="tempPublishDate" hide-header
                                                @update:model-value="setPublishDate" />
                                        </v-menu>
                                    </template>
                                </v-text-field>

                                <!-- <v-text-field v-model="formData.blog_category" label="Category " variant="outlined"
                                    density="compact" class="mb-2" /> -->

                                <div class="text-subtitle-1 font-weight-medium mb-3">
                                    Cover Image
                                </div>

                                <v-img v-if="coverImageUrl" :src="coverImageUrl" height="230" cover
                                    class="rounded-lg mb-4" />


                                <v-sheet v-else height="230" border rounded
                                    class="d-flex align-center justify-center mb-4">
                                    <div class="text-medium-emphasis">
                                        No Cover Image Selected
                                    </div>
                                </v-sheet>

                                <v-btn block color="primary" prepend-icon="mdi-image-plus" :loading="uploading"
                                    @click="$refs.fileInput.click()">
                                    Select Cover Image
                                </v-btn>

                                <input ref="fileInput" type="file" class="d-none" accept="image/*"
                                    @change="onFileSelected" />

                            </v-col>

                            <!-- RIGHT -->
                            <v-col cols="12" md="8" class="pa-6 right-panel">

                                <!-- <v-textarea v-model="formData.blog_content" label="Blog Description *"
                                    variant="outlined" rows="19" auto-grow
                                    :rules="[(v) => !!v || 'Blog Description is required']" /> -->
                                <div class="right-scroll">
                                    <div class="rich-text-editor">
                                        <QuillEditor v-model:content="formData.blog_content" contentType="html"
                                            theme="snow" class="blog-editor" />
                                    </div>
                                </div>

                            </v-col>

                        </v-row>
                    </v-form>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn color="grey" variant="text" @click="handleClose" :disabled="loading">
                        Cancel
                    </v-btn>
                    <v-btn color="primary" variant="flat" :loading="loading" @click="handleSubmit">
                        Update Blog Post
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import { generateClient } from "aws-amplify/api";
import { edit_blog } from "@/graphql/mutations";
import S3AmplifyMixin from "@/mixins/S3Amplify";
import ImageCropperDialog from "@/components/Extras/ImageCropperDialog.vue";
import getBaseFolder from "@/Configurations/s3BaseFolder";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";

const client = generateClient();

export default {
    name: "EditBlogPostDialog",
    components: {
        SnackbarComponent,
        ImageCropperDialog,
        QuillEditor,
    },
    mixins: [S3AmplifyMixin],
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        blogpost: {
            type: Object,
            default: null,
        },
    },
    emits: ["update:modelValue", "updated"],
    data() {
        return {
            cropDialog: false,
            cropFile: null,

            pendingCroppedFile: null,
            pendingPreviewUrl: null,

            coverImageUrl: "",
            publishDate: "",
            publishDateDisplay: "",
            tempPublishDate: null,
            showPublishPicker: false,
            snackbar: {
                show: false,
                message: "",
                color: "success",
            },
            loading: false,
            uploading: false,

            formData: {
                blog_main_title: "",
                blog_category: "",
                blog_author_name: "",
                blog_content: "",
                blog_image_s3_path: "",
                published_on: "",
            },
        };
    },
    computed: {
        dialog: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit("update:modelValue", value);
            },
        },
    },
    watch: {
        modelValue(val) {
            if (val && this.blogpost) {
                this.loadBlogData();
            } else if (!val) {
                this.resetForm();
            }
        },

        blogpost: {
            immediate: true,
            handler(newVal) {
                if (newVal && this.modelValue) {
                    this.loadBlogData();
                }
            },
        },
    },
    methods: {
        generateBlogPageUrl(title) {
            const slug = (title || "")
                .toLowerCase()
                .trim()
                .replace(/[^a-z0-9\s-]/g, "")
                .replace(/\s+/g, "-")
                .replace(/-+/g, "-")
                .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens

            return `https://rcshealth.io/blog-post/${slug}`;
        },
        formatDateInput(e) {
            let value = e.target.value.replace(/\D/g, "").slice(0, 8);

            if (value.length > 2)
                value = value.slice(0, 2) + "/" + value.slice(2);

            if (value.length > 5)
                value = value.slice(0, 5) + "/" + value.slice(5);

            this.publishDateDisplay = value;
        },

        validateAndFormatDate() {
            if (!/^\d{2}\/\d{2}\/\d{4}$/.test(this.publishDateDisplay))
                return;

            const [m, d, y] = this.publishDateDisplay.split("/");

            this.publishDate = Math.floor(
                new Date(`${y}-${m}-${d}T00:00:00`).getTime() / 1000
            );

            this.formData.published_on = this.publishDate;
        },

        setPublishDate(val) {
            const date = new Date(val);

            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, "0");
            const d = String(date.getDate()).padStart(2, "0");

            this.publishDateDisplay = `${m}/${d}/${y}`;

            this.publishDate = Math.floor(
                new Date(`${y}-${m}-${d}T00:00:00`).getTime() / 1000
            );

            this.formData.published_on = this.publishDate;

            this.showPublishPicker = false;
        },
        async loadBlogData() {
            if (!this.blogpost) return;

            this.formData.blog_main_title = this.blogpost.blog_main_title || "";
            this.formData.blog_category = this.blogpost.blog_category || "";
            this.formData.blog_author_name = this.blogpost.blog_author_name || "";

            // Publish Date
            if (this.blogpost.published_on) {
                const timestamp = Number(this.blogpost.published_on);

                // Convert seconds -> milliseconds if required
                const ms = timestamp < 1000000000000
                    ? timestamp * 1000
                    : timestamp;

                const date = new Date(ms);

                if (!isNaN(date.getTime())) {
                    const y = date.getFullYear();
                    const m = String(date.getMonth() + 1).padStart(2, "0");
                    const d = String(date.getDate()).padStart(2, "0");

                    this.publishDateDisplay = `${m}/${d}/${y}`;

                    // Store epoch seconds for update API
                    this.publishDate = Math.floor(ms / 1000);

                    this.tempPublishDate = date;
                    this.formData.published_on = this.publishDate;
                }
            } else {
                this.publishDate = "";
                this.publishDateDisplay = "";
                this.tempPublishDate = null;
                this.formData.published_on = "";
            }

            // Blog Content
            try {
                this.formData.blog_content = this.blogpost.blog_content
                    ? JSON.parse(this.blogpost.blog_content)
                    : "";
            } catch {
                this.formData.blog_content = this.blogpost.blog_content || "";
            }

            // Cover Image
            this.formData.blog_image_s3_path =
                this.blogpost.blog_image_s3_path || "";

            if (this.blogpost.blog_image_s3_path) {
                this.coverImageUrl = await this.getFileUrl(
                    this.blogpost.blog_image_s3_path,
                    "landing"
                );
            } else {
                this.coverImageUrl = "";
            }

            this.pendingCroppedFile = null;
            this.cropFile = null;
        },
        async onFileSelected(event) {
            const file = event.target.files?.[0];

            if (!file) return;

            this.cropFile = file;
            this.cropDialog = true;

            event.target.value = "";
        },

        async uploadBlogCover(file) {
            const key = `${getBaseFolder()}/BlogImages/${Date.now()}-${file.name}`;

            return this.uploadFile(file, key, "landing");
        },

        handleCropCancel() {
            this.cropDialog = false;
            this.cropFile = null;
        },

        handleCropped(file) {
            this.cropDialog = false;

            if (this.pendingPreviewUrl) {
                URL.revokeObjectURL(this.pendingPreviewUrl);
            }

            this.pendingCroppedFile = file;
            this.pendingPreviewUrl = URL.createObjectURL(file);

            this.coverImageUrl = this.pendingPreviewUrl;
        },
        handleClose() {
            this.dialog = false;
            this.resetForm();
        },
        resetForm() {
            this.formData = {
                blog_main_title: "",
                blog_category: "",
                blog_author_name: "",
                blog_content: "",
                blog_image_s3_path: "",
                published_on: "",
            };
            this.publishDate = "";
            this.publishDateDisplay = "";
            this.tempPublishDate = null;
            this.showPublishPicker = false;
            this.coverImageUrl = "";
            this.cropDialog = false;
            this.cropFile = null;
            this.pendingCroppedFile = null;

            if (this.pendingPreviewUrl) {
                URL.revokeObjectURL(this.pendingPreviewUrl);
                this.pendingPreviewUrl = null;
            }

            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },
        async handleSubmit() {
            const plainText = (this.formData.blog_content || "")
                .replace(/<[^>]*>/g, "")
                .trim();

            if (!plainText) {
                this.snackbar = {
                    show: true,
                    message: "Blog Description is required",
                    color: "error",
                };
                return;
            }
            const { valid } = await this.$refs.form.validate();

            if (!valid) return;

            this.loading = true;

            let coverImagePath = this.formData.blog_image_s3_path;

            if (this.pendingCroppedFile) {
                try {
                    this.uploading = true;

                    coverImagePath = await this.uploadBlogCover(
                        this.pendingCroppedFile
                    );

                    this.formData.blog_image_s3_path = coverImagePath;
                } finally {
                    this.uploading = false;
                }
            }

            try {
                const blogPageUrl = this.generateBlogPageUrl(
                    this.formData.blog_main_title
                );
                const input = {
                    blog_id: this.blogpost.blog_id,
                    blog_main_title: this.formData.blog_main_title,
                    blog_category: this.formData.blog_category,
                    blog_author_name: this.formData.blog_author_name,
                    blog_content: JSON.stringify(this.formData.blog_content),
                    blog_image_s3_path: coverImagePath,
                    blog_page_url: blogPageUrl,
                    published_on: this.publishDate,
                };

                const response = await client.graphql({
                    query: edit_blog,
                    variables: {
                        input,
                    },
                });

                const responseData = JSON.parse(
                    response.data.edit_blog
                );

                if (responseData.status === "SUCCESS") {
                    this.snackbar = {
                        show: true,
                        message:
                            responseData.status_message ||
                            "Blog updated successfully!",
                        color: "success",
                    };

                    this.$emit("updated");

                    this.handleClose();
                } else {
                    this.snackbar = {
                        show: true,
                        message:
                            responseData.status_message ||
                            "Failed to update blogpost",
                        color: "error",
                    };
                }
            } catch (error) {
                console.error(error);

                this.snackbar = {
                    show: true,
                    message:
                        error.errors?.[0]?.message ||
                        error.message ||
                        "Failed to update blogpost",
                    color: "error",
                };
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style>
.blog-dialog {
    height: 85vh;
    display: flex;
    flex-direction: column;
}

.blog-dialog .v-card-text {
    flex: 1;
    overflow: hidden;
    padding: 0;
    min-height: 0;
}

.dialog-content {
    height: 100%;
    margin: 0;
}

/* ---------------- LEFT PANEL ---------------- */

.left-panel {
    height: 100%;
    border-right: 1px solid #e0e0e0;

    overflow-y: auto;
    overflow-x: hidden;

    padding: 20px;
}

/* ---------------- RIGHT PANEL ---------------- */

.right-panel {
    height: 100%;
    display: flex;
    overflow: hidden;
    padding: 20px;
}

.right-scroll {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

/* ---------------- QUILL ---------------- */

.rich-text-editor {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.blog-editor {
    flex: 1;
    display: flex;
    flex-direction: column;

    border: 1px solid #ddd;
    border-radius: 8px;

    overflow: hidden;
}

/* Toolbar */

:deep(.ql-toolbar.ql-snow) {
    position: sticky;
    top: 0;
    z-index: 10;

    background: white;

    border: none;
    border-bottom: 1px solid #ddd;

    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
}

/* Container */

:deep(.ql-container.ql-snow) {
    flex: 1;

    overflow-y: auto;

    border: none;

    font-size: 15px;
}

/* Editor */

:deep(.ql-editor) {
    min-height: 100%;
    padding: 18px;

    font-size: 15px;
    line-height: 1.8;
}

:deep(.ql-editor p) {
    margin-bottom: 12px;
}

:deep(.ql-editor h1),
:deep(.ql-editor h2),
:deep(.ql-editor h3),
:deep(.ql-editor h4),
:deep(.ql-editor h5),
:deep(.ql-editor h6) {
    margin-bottom: 12px;
    font-weight: 600;
}

:deep(.ql-editor img) {
    max-width: 100%;
    height: auto;
}

/* Footer */

.blog-dialog .v-card-actions {
    flex-shrink: 0;

    border-top: 1px solid #eee;

    background: white;
}

/* Scrollbars */

.left-panel::-webkit-scrollbar,
:deep(.ql-container)::-webkit-scrollbar {
    width: 8px;
}

.left-panel::-webkit-scrollbar-thumb,
:deep(.ql-container)::-webkit-scrollbar-thumb {
    background: #c7c7c7;
    border-radius: 8px;
}

.left-panel::-webkit-scrollbar-thumb:hover,
:deep(.ql-container)::-webkit-scrollbar-thumb:hover {
    background: #999;
}
</style>
